import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'tutorial');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeSlug);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.ky.app/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: activeCategory.name,
                item: `https://www.ky.app/category/${activeSlug}`,
              },
            ],
          }),
        }}
      />

      <Navbar />
      <main className="pt-16">
        {/* Header */}
        <section className="bg-background-100 py-14 md:py-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto text-center">
              <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500">
                <i className={`${activeCategory.icon} text-2xl`}></i>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-3">
                {activeCategory.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto leading-relaxed">
                {activeCategory.description}
              </p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="border-b border-background-200/70 bg-background-50 sticky top-16 z-30">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-1 overflow-x-auto py-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group p-5 md:p-6 rounded-2xl bg-background-100 hover:bg-background-200/70 transition-colors duration-200 cursor-pointer flex flex-col"
                    >
                      <div className="w-full h-44 md:h-48 rounded-xl overflow-hidden mb-4 bg-background-200">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="flex items-center gap-3 mb-2 text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 flex-1">
                        {article.excerpt}
                      </p>
                      <div className="mt-4 pt-4 border-t border-background-200/70 flex items-center justify-between">
                        <span className="text-xs text-foreground-400">{article.author}</span>
                        <span className="text-xs text-primary-500 font-medium flex items-center gap-1">
                          阅读全文
                          <i className="ri-arrow-right-line"></i>
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-300">
                    <i className="ri-file-list-3-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}